import { getDefaultStore, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  pendingFirstTurnAtom,
  seededFirstTurnProjectIds,
  useRoutineLaunchLock,
  useRunAnyWorkflow,
} from '@heidi/agents/data';
import type {
  WorkflowTriggerSource,
  WorkflowTriggerType,
} from '../../../contracts/routines-analytics.types';
import {
  runningWorkflowIdsAtom,
  withRunningFlag,
} from '../../../data/workflows/state/running';
import type { WorkCreateCommand } from '../../composer/Composer';
import {
  seededFirstTurnFromCommand,
  seededFirstTurnIsEmpty,
} from '../../composer/seeded-first-turn';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import { notifyRunStartedWithoutChat } from './runStartedToast';
import { useOpenRunChat } from './useOpenRunChat';

interface UseRunRoutineToChatOptions {
  triggerSource?: WorkflowTriggerSource;
  /** Defaults to `on_demand`. Shortcuts pass `keyboard`. */
  triggerType?: WorkflowTriggerType;
  /**
   * Where to open a successful run. Defaults to the standalone project chat;
   * callers that already render a chat surface can bind the project in place.
   */
  openChat?: (projectId: string) => Promise<boolean | void> | void;
  /** Called when the kick-off request or caller-supplied destination fails. */
  onFailed?: () => void;
  /** Called when the run succeeds without a project chat to bind. */
  onStartedWithoutChat?: () => void;
}

/**
 * Run a routine the user owns and hand its project id to `openChat`, which
 * defaults to the standard project chat page. The card's byline flips to
 * "Running" while the kick-off round-trip is in flight. A run that starts
 * without a chat shell is confirmed with a toast so the click never reads as
 * dead.
 *
 * An optional Ask-composer command is staged in `pendingFirstTurnAtom` —
 * the same cross-mount hand-off `ProjectChat` already drains for a newly
 * created project — before `openChat` runs, so the run's chat opens with the
 * question, attachments, linked chip, and approval mode as its first turn.
 */
export function useRunRoutineToChat(
  options?: UseRunRoutineToChatOptions
): (workflowId: string, firstTurn?: WorkCreateCommand) => void {
  const intl = useIntl();
  const triggerSource = options?.triggerSource ?? 'work_home_play';
  const triggerType = options?.triggerType ?? 'on_demand';
  const onFailed = options?.onFailed;
  const onStartedWithoutChat = options?.onStartedWithoutChat;
  const runAnyWorkflow = useRunAnyWorkflow();
  const defaultOpenChat = useOpenRunChat(getWorkHostPort('shell').useNavigate());
  const openChat = options?.openChat ?? defaultOpenChat;
  const setRunning = useSetAtom(runningWorkflowIdsAtom);
  const launchLock = useRoutineLaunchLock();

  return useCallback(
    (workflowId: string, firstTurn?: WorkCreateCommand) => {
      const seeded = firstTurn ? seededFirstTurnFromCommand(firstTurn) : null;
      if (seeded && seededFirstTurnIsEmpty(seeded)) return;
      if (!launchLock.tryBegin(workflowId)) return;
      // The card byline / play glyph shows "Running" until the handoff settles.
      setRunning((prev) => withRunningFlag(prev, workflowId, true));
      const settle = () => {
        launchLock.end(workflowId);
        setRunning((prev) => withRunningFlag(prev, workflowId, false));
      };
      runAnyWorkflow.mutate(
        {
          workflowId,
          trigger_source: triggerSource,
          trigger_type: triggerType,
        },
        {
          onSuccess: (res) => {
            const projectId = res.project_id;
            if (projectId) {
              if (seeded) {
                // Never the URL — shared jotai state survives the route change,
                // and `useProjectSeededFirstTurn` submits it once the chat mounts.
                getDefaultStore().set(pendingFirstTurnAtom, {
                  projectId,
                  ...seeded,
                });
                // Show the chat's working state (never the cold empty card)
                // through navigation/remount while the seeded turn lands.
                seededFirstTurnProjectIds.add(projectId);
              }
              // A cancelled route change or rejected caller destination
              // unwinds the seeded turn; settle regardless.
              void Promise.resolve(openChat(projectId))
                .catch(() => {
                  getDefaultStore().set(pendingFirstTurnAtom, (pending) =>
                    pending?.projectId === projectId ? null : pending
                  );
                  seededFirstTurnProjectIds.delete(projectId);
                  onFailed?.();
                })
                .finally(settle);
              return;
            }
            settle();
            onStartedWithoutChat?.();
            notifyRunStartedWithoutChat(intl);
          },
          onError: () => {
            settle();
            onFailed?.();
          },
        }
      );
    },
    [
      intl,
      launchLock,
      onFailed,
      onStartedWithoutChat,
      openChat,
      runAnyWorkflow,
      setRunning,
      triggerSource,
      triggerType,
    ]
  );
}
